import { request } from '@/api/client'
import {
  mapVersionDetail,
  normalizePlatforms,
  toSaveDraftBody,
} from '@/api/versionMapper'
import {
  applyHistoricalToDraft,
  getParseResult,
  normalizeUploadDraft,
  withNeedsPlatformPackages,
  withRuntimes,
} from '@/api/packages'
import type {
  AppVersion,
  Platform,
  PlatformFamily,
  VersionStatus,
} from '@/types'

/**
 * 版本管理接口层。契约以 `api (2).md` / ADR 0003 为准。
 * 本地：同源 `/api` → Vite proxy → `VITE_API_BASE_URL`。
 */

const versionsPath = (app_id: string, version_id?: string) =>
  version_id
    ? `/developer/apps/${app_id}/versions/${version_id}`
    : `/developer/apps/${app_id}/versions`

type RawRecord = Record<string, unknown>

function pick<T>(
  obj: RawRecord | null | undefined,
  camel: string,
  snake: string,
): T | undefined {
  if (!obj) return undefined
  const v = obj[camel] ?? obj[snake]
  return v as T | undefined
}

/** @deprecated 主路径改为上传主包即落草稿；保留兼容 */
export interface CreateDraftBody {
  mainPackageUploadId: string
  platform_packages?: Array<{
    platform: Platform
    upload_id?: string
    package_url?: string
    package_version?: string
    file_name?: string
    fileSize?: number
    is_historical?: boolean
    from_version_id?: string
  }>
}

/** 创建草稿请求体 → 接口文档 snake_case */
function toCreateDraftBody(body: CreateDraftBody): Record<string, unknown> {
  const out: Record<string, unknown> = {
    main_package_upload_id: body.mainPackageUploadId,
  }
  if (body.platform_packages) {
    out.platform_packages = body.platform_packages.map((p) => {
      const item: Record<string, unknown> = {
        platform: p.platform,
      }
      if (p.upload_id !== undefined) item.upload_id = p.upload_id
      if (p.package_url !== undefined) item.package_url = p.package_url
      if (p.package_version !== undefined) item.package_version = p.package_version
      if (p.file_name !== undefined) item.file_name = p.file_name
      if (p.fileSize !== undefined) item.file_size = p.fileSize
      if (p.is_historical !== undefined) item.is_historical = p.is_historical
      if (p.from_version_id !== undefined) item.from_version_id = p.from_version_id
      return item
    })
  }
  return out
}

export interface ListVersionsParams {
  /** 家族名；服务端按「本版新传了该家族任一清单 key」筛 */
  platform?: PlatformFamily
  status?: VersionStatus
  page?: number
  page_size?: number
}

export interface ListVersionsResult {
  list: AppVersion[]
  page: number
  page_size: number
  total: number
  has_collaborators: boolean
  max_online_version?: string
}

/** 列表里的平台可能是字符串，也可能是 { platform } / { type } */
function readPlatforms(raw: RawRecord): Platform[] {
  return normalizePlatforms(pick(raw, 'platforms', 'platforms')) ?? []
}

function mapListItem(raw: RawRecord): AppVersion {
  const operator = pick<{ nickname?: string; username?: string }>(
    raw,
    'operator',
    'operator',
  )
  const uploaded = normalizePlatforms(
    pick(raw, 'uploaded_platforms', 'uploaded_platforms'),
  )
  return {
    id: pick<string>(raw, 'version_id', 'version_id') ?? '',
    version: pick<string>(raw, 'version_code', 'version_code') ?? '',
    platforms: readPlatforms(raw),
    uploaded_platforms: uploaded?.length ? uploaded : undefined,
    status: pick<VersionStatus>(raw, 'status', 'status') ?? 'developing',
    released_at: pick<string | null>(raw, 'released_at', 'released_at') ?? null,
    last_operated_at:
      pick<string>(raw, 'last_operated_at', 'last_operated_at') ??
      new Date().toISOString(),
    scheduled_at:
      pick<string | null>(raw, 'scheduled_at', 'scheduled_at') ?? null,
    operator: operator
      ? {
          nickname: operator.nickname ?? '',
          username: operator.username ?? '',
        }
      : undefined,
  }
}

export async function listVersions(
  app_id: string,
  params: ListVersionsParams = {},
): Promise<ListVersionsResult> {
  const data = await request<RawRecord>(versionsPath(app_id), {
    params: {
      platform: params.platform,
      status: params.status,
      page: params.page ?? 1,
      page_size: params.page_size ?? 20,
    },
  })
  const pagination = (pick<RawRecord>(data, 'pagination', 'pagination') ??
    {}) as RawRecord
  const listRaw = pick<RawRecord[]>(data, 'list', 'list') ?? []

  return {
    list: listRaw.map((item) => mapListItem(item)),
    page: pick<number>(pagination, 'page', 'page') ?? params.page ?? 1,
    page_size:
      pick<number>(pagination, 'page_size', 'page_size') ??
      params.page_size ??
      20,
    total: pick<number>(pagination, 'total', 'total') ?? listRaw.length,
    has_collaborators:
      pick<boolean>(data, 'has_collaborators', 'has_collaborators') ?? false,
    max_online_version: pick<string>(
      data,
      'max_online_version',
      'max_online_version',
    ),
  }
}

/** 列表接口每页最多 20；整表刷新 / 详情降级时翻页拉全量 */
const LIST_PAGE_SIZE = 20

export async function listAllVersions(
  app_id: string,
): Promise<ListVersionsResult> {
  const first = await listVersions(app_id, {
    page: 1,
    page_size: LIST_PAGE_SIZE,
  })
  if (first.total <= first.list.length) return first

  const pageCount = Math.ceil(first.total / LIST_PAGE_SIZE)
  const rest = await Promise.all(
    Array.from({ length: pageCount - 1 }, (_, i) =>
      listVersions(app_id, { page: i + 2, page_size: LIST_PAGE_SIZE }),
    ),
  )
  const list = [...first.list, ...rest.flatMap((p) => p.list)]
  return {
    ...first,
    list,
    page: 1,
    page_size: list.length,
  }
}

export async function getVersion(
  app_id: string,
  version_id: string,
): Promise<AppVersion> {
  const raw = await request<RawRecord>(versionsPath(app_id, version_id))
  let dto = normalizeUploadDraft(raw)

  // 详情主包已带 needs_platform_packages；只读态不再补拉。
  // 草稿 / 驳回仍拉一次 parse-result，只补历史包（详情没给 platforms 时才补平台）。
  const editable = dto.status === 'developing' || dto.status === 'rejected'
  const upload_id = dto.main_package?.upload_id
  if (editable && upload_id) {
    try {
      const parse = await getParseResult(upload_id)
      if (parse.parse_status !== 'failed') {
        if (!dto.platforms?.length && parse.platforms?.length) {
          dto = { ...dto, platforms: parse.platforms }
        }
        dto = applyHistoricalToDraft(
          dto,
          parse.historical_packages ?? [],
          dto.platforms ?? [],
        )
        if (dto.main_package?.needs_platform_packages === undefined) {
          dto = withNeedsPlatformPackages(dto, parse)
        }
        if (!dto.main_package?.runtimes?.length) {
          dto = withRuntimes(dto, parse)
        }
      }
    } catch {
      // 补拉失败不影响详情展示
    }
  }

  return mapVersionDetail(dto)
}

/** @deprecated 见 ADR 0003：上传主包即落草稿 */
export async function createDraftVersion(
  app_id: string,
  body: CreateDraftBody,
): Promise<AppVersion> {
  const data = await request<RawRecord>(versionsPath(app_id), {
    method: 'POST',
    body: toCreateDraftBody(body),
  })

  return {
    id: pick<string>(data, 'version_id', 'version_id') ?? '',
    version: pick<string>(data, 'version_code', 'version_code') ?? '',
    status: pick<VersionStatus>(data, 'status', 'status') ?? 'developing',
    platforms: [],
    released_at: null,
    last_operated_at: new Date().toISOString(),
    scheduled_at: null,
  }
}

export async function saveDraft(
  app_id: string,
  version_id: string,
  patch: Partial<AppVersion>,
): Promise<void> {
  await request(versionsPath(app_id, version_id), {
    method: 'PATCH',
    body: toSaveDraftBody(patch),
  })
}

export interface SubmitReviewResult {
  /** reviewing；免审时可能直接 scheduled / released */
  status: VersionStatus
  scheduled_at: string | null
  released_at: string | null
  scope_snapshot?: AppVersion['scope_snapshot']
  /** 免审首次上架才返回，只此一次 */
  credentials?: { app_id: string; app_secret: string }
}

export async function submitReview(
  app_id: string,
  version_id: string,
  scheduled_at?: string | null,
): Promise<SubmitReviewResult> {
  if (scheduled_at !== undefined) {
    await request(versionsPath(app_id, version_id), {
      method: 'PATCH',
      body: { scheduled_at: scheduled_at },
    })
  }
  const data = await request<RawRecord>(
    versionsPath(app_id, version_id) + '/submit',
    { method: 'POST', body: {} },
  )
  const returnedAppId = pick<string>(data, 'app_id', 'app_id')
  const appSecret = pick<string>(data, 'app_secret', 'app_secret')
  return {
    status: pick<VersionStatus>(data, 'status', 'status') ?? 'reviewing',
    scheduled_at:
      pick<string | null>(data, 'scheduled_at', 'scheduled_at') ?? null,
    released_at: pick<string | null>(data, 'released_at', 'released_at') ?? null,
    scope_snapshot: pick(data, 'scope_snapshot', 'scope_snapshot'),
    credentials:
      returnedAppId && appSecret
        ? { app_id: returnedAppId, app_secret: appSecret }
        : undefined,
  }
}

export async function withdrawReview(
  app_id: string,
  version_id: string,
): Promise<void> {
  await request(versionsPath(app_id, version_id) + '/withdraw', {
    method: 'POST',
    body: {},
  })
}

export async function deleteVersion(
  app_id: string,
  version_id: string,
): Promise<void> {
  await request(versionsPath(app_id, version_id), { method: 'DELETE' })
}

export async function cancelSchedule(
  app_id: string,
  version_id: string,
): Promise<void> {
  await request(versionsPath(app_id, version_id) + '/cancel-schedule', {
    method: 'POST',
    body: {},
  })
}

export async function releaseNow(
  app_id: string,
  version_id: string,
): Promise<void> {
  await request(versionsPath(app_id, version_id) + '/release', {
    method: 'POST',
    body: {},
  })
}

export async function delistVersion(
  app_id: string,
  version_id: string,
): Promise<void> {
  await request(versionsPath(app_id, version_id) + '/delist', {
    method: 'POST',
    body: {},
  })
}

export async function disableVersion(
  app_id: string,
  version_id: string,
): Promise<void> {
  await request(versionsPath(app_id, version_id) + '/disable', {
    method: 'POST',
    body: {},
  })
}

export async function enableVersion(
  app_id: string,
  version_id: string,
): Promise<void> {
  await request(versionsPath(app_id, version_id) + '/enable', {
    method: 'POST',
    body: {},
  })
}

export async function relistVersion(
  app_id: string,
  version_id: string,
): Promise<void> {
  await request(versionsPath(app_id, version_id) + '/relist', {
    method: 'POST',
    body: {},
  })
}

export async function rollbackVersion(
  app_id: string,
  version_id: string,
): Promise<void> {
  await request(versionsPath(app_id, version_id) + '/rollback', {
    method: 'POST',
    body: {},
  })
}

/** 修改定时上线时间（仅改 scheduled_at） */
export async function updateSchedule(
  app_id: string,
  version_id: string,
  scheduled_at: string,
): Promise<void> {
  await request(versionsPath(app_id, version_id) + '/schedule', {
    method: 'PATCH',
    body: { scheduled_at: scheduled_at },
  })
}

/** 上传 PC / 移动端截图 */
export async function uploadScreenshot(input: {
  app_id: string
  version_id: string
  file: File
  kind: 'pc' | 'mobile'
  lang: string
  title: string
}): Promise<{ url: string; width?: number; height?: number; title?: string }> {
  const form = new FormData()
  form.append('file', input.file)
  form.append('kind', input.kind)
  form.append('lang', input.lang)
  form.append('title', input.title)

  const raw = await request<RawRecord>(
    `${versionsPath(input.app_id, input.version_id)}/screenshots`,
    { method: 'POST', body: form },
  )

  return {
    url: pick<string>(raw, 'url', 'url') ?? '',
    width: pick<number>(raw, 'width', 'width'),
    height: pick<number>(raw, 'height', 'height'),
    title: pick<string>(raw, 'title', 'title'),
  }
}

export interface SupportAccountSearchItem {
  username: string
  nickname: string
  avatar_url?: string
  space_name?: string
}

export interface SearchSupportAccountResult {
  list: SupportAccountSearchItem[]
  page: number
  page_size: number
  total: number
}

/** 搜索联系客服账号（keyword 模糊搜 username / nickname） */
export async function searchSupportAccount(
  app_id: string,
  params: { keyword: string; page?: number; page_size?: number } = {
    keyword: '',
  },
): Promise<SearchSupportAccountResult> {
  const keyword = params.keyword.trim().replace(/^@+/, '')
  const page = params.page ?? 1
  const page_size = params.page_size ?? 20
  const raw = await request<RawRecord>(
    `/developer/apps/${app_id}/support-account/search`,
    {
      params: {
        keyword: keyword || undefined,
        page,
        page_size,
      },
    },
  )
  const pagination = (pick<RawRecord>(raw, 'pagination', 'pagination') ??
    {}) as RawRecord
  const listRaw = pick<RawRecord[]>(raw, 'list', 'list') ?? []

  return {
    list: listRaw.map((item) => ({
      username: pick<string>(item, 'username', 'username') ?? '',
      nickname: pick<string>(item, 'nickname', 'nickname') ?? '',
      avatar_url: pick<string>(item, 'avatar_url', 'avatar_url'),
      space_name: pick<string>(item, 'space_name', 'space_name'),
    })),
    page: pick<number>(pagination, 'page', 'page') ?? page,
    page_size: pick<number>(pagination, 'page_size', 'page_size') ?? page_size,
    total: pick<number>(pagination, 'total', 'total') ?? listRaw.length,
  }
}
